import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Grid,
  Typography,
  Popover,
  Button,
  FormControlLabel,
  TextField,
  Checkbox
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteField from '../../otherComponent/DeleteStep';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import Reportform from '../../Reportform';

interface NumberComponentProps {
  values: any;
  index: number;
  setFieldValue: (key: string, value: any) => void;
  onSubmit: (data: any) => void;
  handleChange: (e: any) => void;
  handleBlur: (e: any) => void;
  selectedNodeData: any;
  onDeleteField?: (fieldIndex: number) => void;
  toSentenceCase: (str: string) => string;
  fieldTypeSelector?: ReactNode;
  isSettingsOpen?: boolean;
  onSettingsOpen?: (fieldIndex: number) => void;
  onSettingsClose?: () => void;
}

export default function NumberComponent({
  values,
  index,
  setFieldValue,
  onSubmit,
  handleChange,
  handleBlur,
  selectedNodeData,
  onDeleteField,
  toSentenceCase,
  fieldTypeSelector,
  isSettingsOpen,
  onSettingsOpen,
  onSettingsClose
}: NumberComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const settingsButtonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof isSettingsOpen !== 'boolean') {
      return;
    }

    if (!isSettingsOpen) {
      setAnchorEl(null);
      return;
    }

    if (settingsButtonRef.current) {
      setAnchorEl(settingsButtonRef.current);
    }
  }, [isSettingsOpen]);

  const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onSettingsOpen?.(index);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    onSettingsClose?.();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'edit-number-popover' : undefined;

  const fieldValue = values.fields[index]?.answer || '';
  const settings = values.fields[index]?.settings || {};

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container alignItems="center" spacing={0.5}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
          sx={{
            flexGrow: 1,
            maxWidth: 'calc(100% - 40px)',
            py: 1,
            px: 1,
            border: '1px solid #ccc',
            borderRadius: 1,
            backgroundColor: '#fafafa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minWidth: 0
          }}
        >
          <Typography
            variant="body1"
            sx={{ flexGrow: 1, mr: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            {toSentenceCase(values.fields[index].question || 'Untitled Field')}
          </Typography>
          <FormatListNumberedIcon fontSize="small" sx={{ color: 'gray', flexShrink: 0 }} />
        </Box>

            <Box ref={settingsButtonRef} sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={handleEditClick}>
              <SettingsIcon fontSize="medium" />
            </Box>
            <DeleteField 
              onDeleteField={() => onDeleteField?.(index)}
              selectedNodeData={selectedNodeData}
              fieldIndex={index}
              fieldData={values.fields[index]}
            />
          </Box>
        </Grid>

        <Grid item xs={12} hidden>
          <TextField
            fullWidth
            type="number"
            name={`fields.${index}.answer`}
            value={fieldValue}
            onChange={(e) => setFieldValue(`fields.${index}.answer`, e.target.value)}
            inputProps={{
              min: settings.min,
              max: settings.max,
              step: settings.step
            }}
            size="small"
          />
        </Grid>
      </Grid>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: { width: 300, p: 2 } }}
      >
        <Box>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2} >
            <Typography variant="subtitle1">Number Settings</Typography>
            {fieldTypeSelector ? (
              <Box sx={{ minWidth: 120 }}>
                {fieldTypeSelector}
              </Box>
            ) : null}
          </Box>

          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Label"
                name={`fields.${index}.question`}
                defaultValue={values.fields[index].question}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name={`fields.${index}.description`}
                defaultValue={values.fields[index].description}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
              />
            </Grid>

            {/* <Grid item xs={12}>
              <TextField
                fullWidth
                label="Min"
                type="number"
                value={settings.min || ''}
                onChange={(e) => setFieldValue(`fields.${index}.settings.min`, e.target.value)}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Max"
                type="number"
                value={settings.max || ''}
                onChange={(e) => setFieldValue(`fields.${index}.settings.max`, e.target.value)}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="RegExp"
                type="text"
                value={settings.regexp || ''}
                onChange={(e) => setFieldValue(`fields.${index}.settings.regexp`, e.target.value)}
                size="small"
              />
            </Grid> */}

            <Grid item xs={12} sx={{ mt: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.required || false}
                    onChange={(e) =>
                      setFieldValue(`fields.${index}.settings.required`, e.target.checked)
                    }
                  />
                }
                label="Required"
              />
            </Grid>

            {/* <Grid item xs={12} sx={{ mt: -3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.isSupportFileRequired || false}
                    onChange={(event) =>
                      setFieldValue(
                        `fields.${index}.settings.isSupportFileRequired`,
                        event.target.checked
                      )
                    }
                  />
                }
                label="Is Support File Required"
              />
            </Grid> */}

            {settings.isSupportFileRequired && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name={`fields.${index}.settings.noOfSupportImages`}
                  type="number"
                  label="Number of Images"
                  value={settings.noOfSupportImages || ''}
                  onChange={handleChange}
                  size="small"
                  inputProps={{ min: 1 }}
                />
              </Grid>
            )}

            <Grid item xs={12} sx={{ mt: -3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.report?.enabled || false}
                    onChange={(event) => {
                      const isChecked = event.target.checked;
                      setFieldValue(
                        `fields.${index}.settings.report`,
                        isChecked
                          ? { ...(settings.report || {}), enabled: true }
                          : { enabled: false, uid: null, name: null }
                      );
                    }}
                  />
                }
                label="Report "
              />
            </Grid>

            {settings.report?.enabled && (
              <Grid item xs={12} sx={{ mt: -2 }}>
                <Reportform name={`fields.${index}.settings.report`} />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button size="small" variant="contained" onClick={handleClosePopover}>
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </Box>
  );
}
